/*
  =============================================
  LEMAZALI V2 — Accessibility Center
  File: assets/lemazali-accessibility.js

  Small, dependency-free, deferred. Handles the floating button, the
  preference panel's modal behavior (open/close, focus containment,
  focus restoration), the six preference toggles, and localStorage
  persistence. Coordinates with the mobile drawer (sections/header.liquid)
  via a small custom-event handshake so only one modal-style panel
  owns focus at a time.
  =============================================
*/
(function () {
  var STORAGE_KEY = 'lemazali_accessibility_preferences';
  var supportsInert = 'inert' in HTMLElement.prototype;

  var button = document.getElementById('lmz-a11y-shortcut');
  var panel = document.getElementById('lmz-a11y-panel');
  var backdrop = document.getElementById('lmz-a11y-backdrop');
  var closeBtn = document.getElementById('lmz-a11y-panel-close');
  var resetBtn = document.getElementById('lmz-a11y-reset');
  if (!button || !panel || !backdrop) return;

  var html = document.documentElement;

  var DEFAULTS = { text: 'normal', contrast: false, links: false, font: false, spacing: false, motion: false };

  function loadPrefs() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return Object.assign({}, DEFAULTS);
      var parsed = JSON.parse(raw);
      return Object.assign({}, DEFAULTS, parsed);
    } catch (e) {
      return Object.assign({}, DEFAULTS);
    }
  }

  function savePrefs(prefs) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
      /* localStorage unavailable (private browsing, quota, etc.) —
         preferences simply won't persist across visits; the controls
         still work for the current session. */
    }
  }

  var prefs = loadPrefs();

  function applyPrefs() {
    if (prefs.text && prefs.text !== 'normal') {
      html.setAttribute('data-a11y-text', prefs.text);
    } else {
      html.removeAttribute('data-a11y-text');
    }
    toggleAttr('data-a11y-contrast', 'high', prefs.contrast);
    toggleAttr('data-a11y-links', 'highlight', prefs.links);
    toggleAttr('data-a11y-font', 'readable', prefs.font);
    toggleAttr('data-a11y-spacing', 'increased', prefs.spacing);
    toggleAttr('data-a11y-motion', 'reduced', prefs.motion);

    // Reflect state on the controls themselves
    var textButtons = panel.querySelectorAll('[data-a11y-text-btn]');
    textButtons.forEach(function (btn) {
      var isActive = btn.getAttribute('data-a11y-text-btn') === (prefs.text || 'normal');
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    var toggleButtons = panel.querySelectorAll('[data-a11y-action]');
    toggleButtons.forEach(function (btn) {
      var action = btn.getAttribute('data-a11y-action');
      btn.setAttribute('aria-pressed', prefs[action] ? 'true' : 'false');
    });
  }

  function toggleAttr(name, value, on) {
    if (on) { html.setAttribute(name, value); } else { html.removeAttribute(name); }
  }

  applyPrefs();

  // ---------- Preference controls ----------
  panel.querySelectorAll('[data-a11y-text-btn]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      prefs.text = btn.getAttribute('data-a11y-text-btn');
      applyPrefs();
      savePrefs(prefs);
    });
  });

  panel.querySelectorAll('[data-a11y-action]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var action = btn.getAttribute('data-a11y-action');
      prefs[action] = !prefs[action];
      applyPrefs();
      savePrefs(prefs);
    });
  });

  if (resetBtn) {
    resetBtn.addEventListener('click', function () {
      prefs = Object.assign({}, DEFAULTS);
      applyPrefs();
      savePrefs(prefs);
    });
  }

  // ---------- Modal open/close (mirrors the mobile drawer's pattern) ----------
  function getFocusable(container) {
    return Array.prototype.slice.call(
      container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
    );
  }

  function getOtherModalTargets() {
    var targets = [];
    var skipLink = document.querySelector('.lmz-skip-link');
    var siteHeader = document.querySelector('.lmz-site-header');
    var main = document.getElementById('MainContent');
    var footer = document.querySelector('.lmz-footer');
    // The floating shortcut that triggers this very panel is also
    // isolated while the panel is open — while modal, nothing outside
    // the dialog (including its own trigger) should be reachable.
    [skipLink, button, siteHeader, main, footer].forEach(function (el) {
      if (el) targets.push(el);
    });
    return targets;
  }

  function setBackgroundIsolated(isolated) {
    getOtherModalTargets().forEach(function (el) {
      if (supportsInert) {
        el.inert = isolated;
      } else if (isolated) {
        el.setAttribute('aria-hidden', 'true');
      } else {
        el.removeAttribute('aria-hidden');
      }
    });
  }

  function setPanelInert(isolated) {
    if (supportsInert) {
      panel.inert = isolated;
      return;
    }
    var focusable = getFocusable(panel);
    if (isolated) {
      focusable.forEach(function (el) {
        if (!el.hasAttribute('data-lmz-prev-tabindex')) {
          el.setAttribute('data-lmz-prev-tabindex', el.getAttribute('tabindex') || '');
        }
        el.setAttribute('tabindex', '-1');
      });
    } else {
      focusable.forEach(function (el) {
        var prev = el.getAttribute('data-lmz-prev-tabindex');
        if (prev) { el.setAttribute('tabindex', prev); } else { el.removeAttribute('tabindex'); }
        el.removeAttribute('data-lmz-prev-tabindex');
      });
    }
  }
  if (!supportsInert) setPanelInert(true);

  function isOpen() { return panel.classList.contains('is-open'); }

  function trapTab(e) {
    if (!isOpen() || e.key !== 'Tab') return;
    var focusable = getFocusable(panel);
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var current = document.activeElement;

    // The panel title is focused programmatically on open via
    // tabindex="-1" — deliberately outside the normal tab sequence, so
    // it's never part of `focusable`. Tabbing/Shift+Tabbing from there
    // (or from anywhere else focus might unexpectedly end up while the
    // panel is open) needs an explicit, deterministic recovery rather
    // than trusting the browser's own calculation, which can vary once
    // everything outside the panel is inert.
    if (focusable.indexOf(current) === -1) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }

    if (e.shiftKey && current === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && current === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function openPanel() {
    // Only one modal-style panel owns focus at a time — ask any other
    // (e.g. the mobile nav drawer) to close first.
    document.dispatchEvent(new CustomEvent('lmz:before-modal-open', { detail: { id: 'a11y-panel' } }));

    setPanelInert(false);
    panel.classList.add('is-open');
    backdrop.classList.add('is-open');
    panel.removeAttribute('aria-hidden');
    button.setAttribute('aria-expanded', 'true');
    document.body.classList.add('lmz-scroll-lock');
    setBackgroundIsolated(true);
    document.addEventListener('keydown', trapTab);

    var title = document.getElementById('lmz-a11y-panel-title');
    (title || panel).setAttribute('tabindex', title ? (title.getAttribute('tabindex') || '-1') : '-1');
    (title || panel).focus();
  }

  function closePanel() {
    panel.classList.remove('is-open');
    backdrop.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    button.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('lmz-scroll-lock');
    setBackgroundIsolated(false);
    document.removeEventListener('keydown', trapTab);
    setPanelInert(true);
    button.focus();
  }

  button.addEventListener('click', function () {
    isOpen() ? closePanel() : openPanel();
  });
  if (closeBtn) closeBtn.addEventListener('click', closePanel);
  backdrop.addEventListener('click', closePanel);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) closePanel();
  });

  // If the mobile nav drawer (or anything else) is about to open,
  // close this panel first so only one modal owns focus.
  document.addEventListener('lmz:before-modal-open', function (e) {
    if (e.detail && e.detail.id !== 'a11y-panel' && isOpen()) closePanel();
  });
})();
